import { useGetPaymentMethodsQuery } from "../../store/api";
import {
  dt,
  formatCents,
  localTimeFromUTCtime,
  phoenixTime,
} from "../../util/stripe";
import { differenceInDays, isAfter } from "date-fns";
import { BsPencilSquare } from "react-icons/bs";
import { HiOutlineDuplicate } from "react-icons/hi";
import { IoReload, IoTrash } from "react-icons/io5";
import { MdWarning } from "react-icons/md";
import { twMerge } from "tailwind-merge";

import PaymentStatus from "./PaymentStatus";

const PaymentEntry = (props) => {
  const { payment, setPauseDialog, editPayment, productName } = props;
  const {
    deal_id,
    payment_method,
    customer_id,
    payment_date,
    status,
    id: paymentId,
    retry,
    countEntries,
    settled,
    dateDifference,
    tax_added,
    amount_cents,
    card_info,
    term_description,
  } = payment || {};
  const { data: cards = [] } = useGetPaymentMethodsQuery({
    customerId: customer_id,
  });
  const has_card = cards.map((method) => method?.id).includes(payment_method);

  // to evaluate "future?" we should get date in PHOENIX TIMEZONE
  const now = phoenixTime();

  const paymentDateVal = dt(payment_date);

  const paidToday = !differenceInDays(now, paymentDateVal);

  const paymentDate = localTimeFromUTCtime(paymentDateVal); // local rep, not phoenix nor utc.
  const past = isAfter(now, paymentDate);

  const dateOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  return (
    <div
      className={twMerge(
        "flex flex-row justify-between px-4",
        (past || status === "success") && "text-gray-400",
        paidToday && "bg-yellow-50",
      )}
      key={"scheduledPayment_" + paymentId}
    >
      {(!!retry || status === "failed") && countEntries > 1 && (
        <div
          className={twMerge(
            "mr-4 w-[15px] bg-red-100",
            settled && "bg-green-100",
          )}
        ></div>
      )}
      <div className="mb-4 flex w-full">
        <div className="mt-3 w-full">
          <b>{paymentDate.toLocaleDateString("en-US", dateOptions)}</b>
          {dateDifference ? ` (${dateDifference})` : ""}
          {" " + term_description}
          <p title={`Deal ${deal_id}`}>
            <i>for</i>
            {` ${productName}`}
          </p>
          <div>
            <i>on</i>
            {" " + card_info}
            {!has_card && status !== "success" && (
              <div className="flex gap-1 text-red-500">
                <MdWarning />
                <small>Not in the list of current cards</small>
              </div>
            )}
          </div>
        </div>

        {/* right block: Amount, status and Tools */}
        <div className="flex flex-col items-center">
          <div
            className="flex justify-end"
            title={
              past
                ? tax_added
                  ? `Including tax ${formatCents(
                      tax_added,
                    )}. Net was ${formatCents(amount_cents)}`
                  : "No tax added"
                : "Tax will be added when charged" //
            }
          >
            {formatCents(+amount_cents + (tax_added || 0))}
          </div>
          {past || status === "success" ? (
            <div className="flex flex-col items-end">
              {settled ? (
                <HiOutlineDuplicate
                  onClick={() => props.onCopy(payment)}
                  className="mx-1 cursor-pointer"
                />
              ) : (
                <IoReload
                  onClick={() => props.onCopy(payment)}
                  className="mx-1 cursor-pointer"
                />
              )}
              <PaymentStatus payment={payment} />
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <div className="flex">
                <HiOutlineDuplicate
                  onClick={() => props.onCopy(payment)}
                  className="mx-1 cursor-pointer"
                />
                <BsPencilSquare
                  color="blue"
                  className="mx-1 cursor-pointer"
                  onClick={() => editPayment(payment)}
                />
                <IoTrash
                  title="Delete this payment"
                  onClick={() =>
                    setPauseDialog(() => ({
                      id: paymentId,
                      newScheduledState: false,
                    }))
                  }
                  className="mx-1 cursor-pointer text-red-500"
                />
              </div>
              <PaymentStatus payment={payment} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentEntry;
